import { CheckCircle2, AlertCircle } from 'lucide-react';
import type { AppointmentTypeRecommendation, ScoreContribution } from '@vagusx/clients';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

/**
 * Shared, presentation-only building blocks for rendering an appointment-type
 * recommendation + its reasons. Extracted from `AppointmentTypeSimulatorDialog`
 * so the Tasks Overview referral card and the Settings simulator render the
 * SAME explanation UI. Nothing here mutates state or records preferences — the
 * interactive tie-resolution UI stays in the simulator dialog.
 */

// =============================================================================
// Confidence Badge
// =============================================================================

const CONFIDENCE_MAP: Record<string, { label: string; className: string }> = {
  high: { label: 'High confidence', className: 'bg-green-100 text-green-800 border-green-200' },
  medium: {
    label: 'Medium confidence',
    className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  },
  low: { label: 'Low confidence', className: 'bg-orange-100 text-orange-800 border-orange-200' },
  none: { label: 'No match', className: 'bg-red-100 text-red-800 border-red-200' },
};

export function ConfidenceBadge({ confidence }: { confidence: string }): React.ReactElement {
  const config = CONFIDENCE_MAP[confidence] ?? CONFIDENCE_MAP['none']!;
  return (
    <Badge variant="outline" className={cn('text-xs font-medium', config.className)}>
      {config.label}
    </Badge>
  );
}

// =============================================================================
// Contribution Helpers
// =============================================================================

/** Convert a contribution signal into a plain-language sentence. */
export function describeContribution(c: ScoreContribution): string {
  if (c.signal === 'CPT code match') {
    return `Procedure codes match${c.detail ? ` (${c.detail})` : ''}`;
  }
  if (c.signal === 'Diagnosis code match') {
    return `Diagnosis codes match${c.detail ? ` (${c.detail})` : ''}`;
  }
  if (c.signal === 'Requires referral') {
    return 'This type is specifically for referred patients';
  }
  if (c.signal === 'Urgency match') {
    return 'Marked for urgent referrals';
  }
  if (c.signal.startsWith('Patient type match')) {
    return `Matches the patient type on the referral`;
  }
  if (c.signal === 'Generic fallback type') {
    return 'Generic catch-all type (lower priority)';
  }
  return c.signal;
}

/** Build a plain-language summary of why the winner was selected. */
export function buildWinnerSummary(result: AppointmentTypeRecommendation): string | null {
  if (!result.appointmentTypeId || result.isTie) return null;

  const winner = result.explanation.allTypes.find(
    (t) => t.appointmentTypeId === result.appointmentTypeId && !t.eliminated
  );
  if (!winner || winner.contributions.length === 0) return null;

  const reasons = winner.contributions
    .filter((c) => c.points > 0)
    .map((c) => describeContribution(c));

  if (reasons.length === 0) return null;
  if (reasons.length === 1) return reasons[0]!;
  return reasons.slice(0, -1).join(', ') + ', and ' + reasons[reasons.length - 1];
}

// =============================================================================
// Recommendation normalization
//
// The simulate query returns one row per provider+location pair, so the same
// appointment type can appear multiple times — and a "tie" between duplicates
// of the same type is not a real tie. Dedup by type id (keep highest score)
// and collapse single-type ties into a winner.
// =============================================================================

export interface NormalizedRecommendation {
  effectiveResult: AppointmentTypeRecommendation;
  scored: AppointmentTypeRecommendation['explanation']['allTypes'];
  eliminatedCount: number;
}

export function normalizeRecommendation(
  result: AppointmentTypeRecommendation
): NormalizedRecommendation {
  const scoredRaw = result.explanation.allTypes
    .filter((t) => !t.eliminated)
    .slice()
    .sort((a, b) => b.score - a.score);
  const seen = new Set<string>();
  const scored = scoredRaw.filter((t) => {
    if (seen.has(t.appointmentTypeId)) return false;
    seen.add(t.appointmentTypeId);
    return true;
  });
  const eliminatedCount = result.explanation.allTypes.filter((t) => t.eliminated).length;

  const uniqueTiedIds = [...new Set(result.tiedTypeIds)];
  const isRealTie = uniqueTiedIds.length > 1;

  const effectiveResult: AppointmentTypeRecommendation = {
    ...result,
    isTie: isRealTie,
    tiedTypeIds: uniqueTiedIds,
    appointmentTypeId:
      !isRealTie && uniqueTiedIds.length === 1 ? uniqueTiedIds[0]! : result.appointmentTypeId,
    appointmentTypeName:
      !isRealTie && uniqueTiedIds.length === 1
        ? (scored.find((t) => t.appointmentTypeId === uniqueTiedIds[0])?.appointmentTypeName ??
          result.appointmentTypeName)
        : result.appointmentTypeName,
  };

  return { effectiveResult, scored, eliminatedCount };
}

// =============================================================================
// Read-only reasons detail
// =============================================================================

/**
 * Read-only breakdown of every considered appointment type and the signals
 * that contributed to (or against) its score. No tie-resolution controls —
 * use this wherever the recommendation is being displayed for review.
 */
export function RecommendationReasons({
  result,
}: {
  result: AppointmentTypeRecommendation;
}): React.ReactElement {
  const { effectiveResult, scored, eliminatedCount } = normalizeRecommendation(result);

  const visibleTypes = scored.filter((t) => {
    const isWinner = t.appointmentTypeId === effectiveResult.appointmentTypeId;
    const isTied = effectiveResult.tiedTypeIds.includes(t.appointmentTypeId);
    const hasSignals = t.contributions.some((c) => c.points > 0);
    return isWinner || isTied || hasSignals;
  });

  return (
    <div className="space-y-3">
      {result.explanation.fallbackPassUsed > 1 && (
        <div className="flex items-center gap-2 rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          No exact code match was found. Results are based on other matching criteria.
        </div>
      )}

      {effectiveResult.explanation.appliedPreference && (
        <div className="space-y-1 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
          <p>{effectiveResult.explanation.appliedPreference.aiReasoning}</p>
          <p className="text-xs italic text-green-700">
            Based on a previous staff decision: "
            {effectiveResult.explanation.appliedPreference.reason}"
          </p>
        </div>
      )}

      {visibleTypes.length > 0 && (
        <div className="space-y-2">
          {visibleTypes.map((type) => {
            const isWinner = type.appointmentTypeId === effectiveResult.appointmentTypeId;
            const isTied =
              effectiveResult.isTie && effectiveResult.tiedTypeIds.includes(type.appointmentTypeId);
            const positiveReasons = type.contributions.filter((c) => c.points > 0);
            const negativeReasons = type.contributions.filter((c) => c.points < 0);

            return (
              <div
                key={type.appointmentTypeId}
                className={cn(
                  'space-y-1.5 rounded-lg border p-3',
                  isWinner && !isTied && 'border-green-300 bg-green-50/50'
                )}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{type.appointmentTypeName}</span>
                  {isTied && (
                    <Badge
                      variant="outline"
                      className="shrink-0 border-yellow-300 text-xs text-yellow-800"
                    >
                      Tied
                    </Badge>
                  )}
                </div>

                {positiveReasons.length > 0 && (
                  <ul className="space-y-0.5">
                    {positiveReasons.map((c, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-1.5 text-xs text-muted-foreground"
                      >
                        <CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0 text-green-500" />
                        {describeContribution(c)}
                      </li>
                    ))}
                  </ul>
                )}
                {negativeReasons.length > 0 && (
                  <ul className="space-y-0.5">
                    {negativeReasons.map((c, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-1.5 text-xs text-muted-foreground"
                      >
                        <AlertCircle className="mt-0.5 h-3 w-3 shrink-0 text-orange-400" />
                        {describeContribution(c)}
                      </li>
                    ))}
                  </ul>
                )}
                {positiveReasons.length === 0 && negativeReasons.length === 0 && (
                  <p className="text-xs text-muted-foreground">No specific matching signals</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {eliminatedCount > 0 && (
        <p className="text-xs text-muted-foreground">
          {eliminatedCount} other type{eliminatedCount !== 1 ? 's were' : ' was'} not eligible based
          on the referral signals.
        </p>
      )}
    </div>
  );
}
